import type {
  LinkableDeliveryNotesQueryParams,
  LinkableDeliveryNotesResponse,
} from './types';

import {
  type DefaultError,
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysInvoice } from '../queryKeys';

import { fetchLinkableDeliveryNotes } from './fetchLinkableDeliveryNotes';

type LinkableDeliveryNotesQueryOptions<TData> = Omit<
  UseQueryOptions<LinkableDeliveryNotesResponse, DefaultError, TData>,
  'queryKey' | 'queryFn'
>;

/**
 * Generates the query options for one page of the delivery notes that can be linked to an invoice.
 *
 * @param queryParams - The invoice id plus search, filter and paging params.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export -- canonical query-options API for route loaders
export function getLinkableDeliveryNotesQueryOptions<
  TData = LinkableDeliveryNotesResponse,
>({
  queryParams,
  options,
}: {
  queryParams: LinkableDeliveryNotesQueryParams;
  options?: LinkableDeliveryNotesQueryOptions<TData>;
}) {
  return queryOptions<LinkableDeliveryNotesResponse, DefaultError, TData>({
    placeholderData: keepPreviousData,
    queryFn: (context: QueryFunctionContext) =>
      fetchLinkableDeliveryNotes(queryParams, context),
    meta: { suppressError: true },
    queryKey: queryKeysInvoice.getLinkableDeliveryNotes(queryParams),
    staleTime: ms('30s'),
    // A failed page renders in the dialog, neither as the error boundary nor as a toast.
    throwOnError: false,
    ...options,
  });
}

/**
 * React Query based custom hook for one page of the delivery notes that can be linked to an invoice.
 *
 * @param queryParams - The invoice id plus search, filter and paging params.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices
 */
export function useQueryLinkableDeliveryNotes<
  TData = LinkableDeliveryNotesResponse,
>(
  queryParams: LinkableDeliveryNotesQueryParams,
  options?: LinkableDeliveryNotesQueryOptions<TData>,
) {
  return useQuery(
    getLinkableDeliveryNotesQueryOptions<TData>({ options, queryParams }),
  );
}
